import React, { useState, useCallback } from 'react';
import { Card, Col, Form, Row } from 'react-bootstrap';
import { Helmet } from 'react-helmet';
import classNames from 'classnames';

import { ProgramModel } from '@/lib/models';
import { navigationService, programSettingService } from '@/lib/services';
import { useHandleError, useAccount, useNavigationConfig } from '@/hooks';
import { AsyncPage } from '@/components';

export const ProgramContent = () => {
	const handleError = useHandleError();
	const { account } = useAccount();
	const { setNavigationConfig } = useNavigationConfig();

	const [isLoading, setIsLoading] = useState(false);
	const [programs, setPrograms] = useState<ProgramModel[]>([]);

	const fetchData = useCallback(async () => {
		if (!account) {
			throw new Error('account is undefined.');
		}

		const response = await programSettingService.getPreferences(account.accountId).fetch();
		setPrograms(response.programs);
	}, [account]);

	const handleProgramCheckboxChange = useCallback(
		async ({ currentTarget }: React.ChangeEvent<HTMLInputElement>) => {
			setIsLoading(true);

			try {
				if (!account) {
					throw new Error('account is undefined.');
				}

				const programResponse = await programSettingService
					.setPreferences(account.accountId, {
						accountCommPreferences: programs
							.map((p) =>
								p.programId === currentTarget.value
									? { ...p, allowAdvisoryDisplay: currentTarget.checked }
									: p
							)
							.filter(({ allowUserPreferences }) => allowUserPreferences)
							.map(({ programId, allowEmail, allowAdvisoryDisplay }) => ({
								allowAdvisoryDisplay,
								allowEmails: allowEmail,
								programId,
							})),
					})
					.fetch();
				const navigationResponse = await navigationService.getNavigationItems().fetch();

				setPrograms(programResponse.programs);
				setNavigationConfig(navigationResponse);
			} catch (error) {
				handleError(error);
			} finally {
				setIsLoading(false);
			}
		},
		[account, handleError, programs, setNavigationConfig]
	);

	return (
		<AsyncPage fetchData={fetchData}>
			<Helmet>
				<title>Hackett Connect - Account - Program Content</title>
			</Helmet>
			<Row>
				<Col>
					<Card>
						<Card.Header>
							<Card.Title className="large">Program Content</Card.Title>
						</Card.Header>
						<Card.Body>
							<p className="mb-6">
								Control the program content that appears in your Hackett Connect<sup>&reg;</sup> portal. Program content
								includes Research, Best Practices, Benchmark Metrics, Events, Advisors, Performance
								Studies, and AI Use Cases.
							</p>
							{programs.filter(({ allowUserPreferences }) => allowUserPreferences).length <= 0 && (
								<p className="mb-0">No programs</p>
							)}
							<Form>
								<fieldset disabled={isLoading}>
									{programs
										.filter(({ allowUserPreferences }) => allowUserPreferences)
										.map(({ programId, name, allowAdvisoryDisplay }, programIndex) => {
											const isLast = programs.length - 1 === programIndex;

											return (
												<Form.Check
													key={programId}
													type="checkbox"
													className={classNames({ 'mb-4': !isLast })}
													name="program-content"
													id={`program-content-${programId}`}
													label={name}
													value={programId}
													checked={allowAdvisoryDisplay}
													onChange={handleProgramCheckboxChange}
												/>
											);
										})}
								</fieldset>
							</Form>
						</Card.Body>
					</Card>
				</Col>
			</Row>
		</AsyncPage>
	);
};
